<template>
    <div
        class="grid-item"
        v-click-outside="closeMenu"
    >
        <div class="header-search-bar">
            <v-popper2
                :state="showResults"
                slide-from="top"
                direction="horizontal"
                position="bottomStart"
                class="display-block"
            >
                <template #trigger>
                    <r-input
                        ref="search-field"
                        placeholder="Search by name, organization or id of any customer, transaction, invoice, or order"
                        :left-icon="isLoading ? 'loading' : 'search'"
                        :left-icon-spinning="isLoading"
                        v-model="searchTerm"
                        @input="clearResults"
                        @key-submit="handleSubmit"
                        @focus="searchFocused = true"
                        @blur="searchFocused = false"
                    >
                        <r-button
                            slot="right-button"
                            class="no-flex"
                            :type="searchTerm ? 'primary' : 'default'"
                            :disabled="!searchTerm"
                            @click="handleSubmit"
                        >
                            Search
                        </r-button>
                    </r-input>
                </template>
                <template #content>
                    <div
                        @mouseenter="disableBodyScroll"
                        @mouseleave="enableBodyScroll">
                        <search-results
                            :results="fetchedData"
                            :query="query"
                            @clear="resetSearch"
                        />
                    </div>
                </template>
            </v-popper2>
        </div>
    </div>
</template>

<script>
    import shortid from 'shortid';
    import vPopper2 from '@/components/popper/v-popper2';
    import SearchResults from '@/components/search/search-results';
    import CustomerModel from '@/common/models/customer-model';
    import InvoiceModel from '@/common/models/invoice-model';
    import OrderModel from '@/common/models/order-model';
    import TransactionModel from '@/common/models/transaction-model';
    import Shortcuts from '@/common/shortcuts/shortcuts';

    export default {
        name: 'search',
        components: {
            SearchResults,
            vPopper2,
        },
        data() {
            return {
                searchTerm: '',
                searchFocused: false,
                fetchedData: {},
                showResults: false,
                query: {},
                queuedSearches: [],
                isParsing: false,
                shortcuts: [],
                lastSearchId: null,
                cancelLastSearchRequest: false,
            };
        },
        watch: {
            $route(route) {
                this.fetchedData = {};
                this.searchFromUrl(route);
                this.closeMenu();
            },
            showResults(newValue) {
                if (newValue === true) {
                    if (!document.activeElement || document.activeElement === document.body) {
                        this.$nextTick(() => {
                            this.focusInput();
                        });
                    }
                }
            },
        },
        computed: {
            isLoading() {
                return !this.cancelLastSearchRequest && this.queuedSearches.length > 0;
            },
        },
        methods: {
            searchFromUrl(route) {
                if (this.isParsing) {
                    return;
                }
                this.isParsing = true;
                if (route.query) {
                    this.searchTerm = route.query.q || '';
                } else {
                    this.searchTerm = '';
                }
                this.isParsing = false;
            },
            async runSearch(query) {
                if (query && (query.clearResults)) {
                    this.fetchedData = {};
                }
                if (query && query.closeResults) {
                    this.$nextTick(() => this.closeMenu());
                }

                if (query && query.params) {
                    this.closeMenu();

                    const handler = async (params, id) => {
                        try {
                            const {q} = params;
                            if (this.$route.name === 'search-results') {
                                return {
                                    route: {
                                        name: 'search-results',
                                        query: {
                                            q,
                                        },
                                    },
                                };
                            }
                            const {fields} = await this.$api(({search}) => search.get({
                                q,
                            }));
                            let {
                                customers, invoices, orders, transactions,
                            } = fields;

                            customers = customers ? customers.map(customer => new CustomerModel(customer)) : [];
                            invoices = invoices ? invoices.map(invoice => new InvoiceModel(invoice)) : [];
                            orders = orders ? orders.map(order => new OrderModel(order)) : [];
                            transactions = transactions ? transactions.map(transaction => new TransactionModel(transaction)) : [];

                            return {
                                focus: true,
                                fetchedData: {
                                    customers,
                                    invoices,
                                    orders,
                                    transactions,
                                },
                                query: params,
                            };
                        } catch (error) {
                            if (error.status === 400 || error.status === 403) {
                                return {
                                    fetchedData: {
                                        customers: [],
                                        invoices: [],
                                        orders: [],
                                        transactions: [],
                                    },
                                    query: query.params,
                                };
                            }
                            return {error};
                        } finally {
                            this.$nextTick(() => {
                                // get last queue element for processing
                                const {lastSearchId} = this;
                                const latest = lastSearchId
                                    ? this.queuedSearches.find(item => item.id === lastSearchId)
                                    : undefined;

                                // delete active request from the queuedSearches list
                                this.queuedSearches = this.queuedSearches.filter(item => item.id !== id);

                                // process latest results
                                const callback = (resp) => {
                                    if (this.cancelLastSearchRequest) {
                                        this.openMenu();
                                        return;
                                    }
                                    this.cancelLastSearchRequest = false;

                                    if (resp && resp.error) {
                                        // render error
                                        this.$toast.fromError(resp.error);
                                    } else if (resp.route) {
                                        this.$router.push(resp.route);
                                    } else if (resp.fetchedData) {
                                        // update component results property
                                        this.fetchedData = {
                                            ...resp.fetchedData,
                                        };

                                        // update component query property
                                        this.query = resp.query;

                                        // open popup menu
                                        this.openMenu();

                                        // focus editable input
                                        if (resp.focus) {
                                            this.$nextTick(() => {
                                                this.focusInput();
                                            });
                                        }
                                    } else {
                                        this.cancelLastSearchRequest = false;
                                        this.fetchedData = {
                                            customers: [],
                                            invoices: [],
                                            orders: [],
                                            transactions: [],
                                        };
                                        this.query = {};
                                        this.openMenu();
                                    }
                                };

                                if (latest) {
                                    latest.handler
                                        .then(callback)
                                        .catch(callback);
                                }
                            });
                        }
                    };

                    const id = shortid.generate();

                    this.lastSearchId = id;
                    this.cancelLastSearchRequest = false;
                    this.queuedSearches.push({
                        id,
                        params: query.params,
                        handler: handler(query.params, id),
                    });
                } else {
                    this.lastSearchId = null;
                    this.openMenu();
                }
            },
            handleSubmit() {
                const {searchTerm} = this;
                if (searchTerm) {
                    this.runSearch({
                        params: {
                            q: searchTerm,
                        },
                    });
                }
            },
            searchCancel() {
                this.cancelLastSearchRequest = true;
            },
            resetSearch() {
                this.clearResults();
                this.searchTerm = '';
                this.blurInput();
            },
            clearResults() {
                this.closeMenu();
            },
            focus() {
                this.openMenu();
            },
            openMenu() {
                this.showResults = true;
            },
            closeMenu() {
                this.showResults = false;
                this.enableBodyScroll();
            },
            focusInput() {
                this.$refs['search-field'].getFocus();
            },
            blurInput() {
                this.$refs['search-field'].blur();
            },
            triggerSearch() {
                this.$refs['search-field'].$emit('trigger-search');
            },
            disableBodyScroll() {
                document.body.classList.add('no-scroll');
            },
            enableBodyScroll() {
                document.body.classList.remove('no-scroll');
            },
        },
        mounted() {
            this.searchFromUrl(this.$route);
        },
        created() {
            this.shortcuts = [
                Shortcuts.register('s', () => {
                    if (!document.activeElement
                        || document.activeElement === document.body
                        || document.activeElement.classList.contains('btn')) {
                        this.focusInput();
                    }
                }),
            ];
        },
        beforeDestroy() {
            this.shortcuts.forEach(shortcut => shortcut.unregister());
            this.enableBodyScroll();
        },
    };
</script>
